import { FC, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { DsmIcon, DsmButton, DsmGrid } from "@dsm-dcs/design-system-react";
import { UserProfilePrefs } from "../../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../../utils/unit-utils";
import { useIntl } from "../../../../../../../_metronic/i18n/customUseIntl";
import { FormType, MMSSystemsBlockProps, ListEntry } from "../../../common";
import ReactHookDsmSelect from "../../../../../../modules/Helpers/ReactHookDsmSelect2";
import ReactHookDsmInput from "../../../../../../modules/Helpers/ReactHookDsmInput2";
import { processAndStageStylesV2 } from "../../../../../../../_metronic/layout";
import { getManureFormForSingleEntry } from "../../../../../helpers/manureManagementSystemsLookups";
import { ManureManagementSystem } from "../../../../../models/Baseline/PigBaseline";
import { ManureForm } from "../../../../../../../graphql/generated/blonk/pigs";

const MMSBlockComponent: FC<MMSSystemsBlockProps> = ({
  stageIndex = 0,
  itemIndex = 0,
  formType = FormType.View,
  manureManagementSystems,
  filteredMMSList,
  removeHandler,
  mmsChangeHandler,
  isAdditions = false,
}) => {
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userProfile = UserProfilePrefs.getInstance();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const userUOM = userProfile.getUserUnitPrefs();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const barnOutputMassUnit = userUOM?.unitBarnOutputMass
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      unitLong(userUOM.unitBarnOutputMass)
    : "kg";

  const classes = processAndStageStylesV2() as any;

  const fieldItemPrefix = `stages.${stageIndex}.stageData.housing.manureSystems${
    isAdditions ? "Additions" : ""
  }.${itemIndex}`;
  const [matchingStorages, setMatchingStorages] = useState<ListEntry[]>([]);
  const formContext = useFormContext();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { getValues, setValue } = useFormContext();

  const showShare = (): boolean => {
    const currentLocalManureForm = getValues(
      `${fieldItemPrefix}.localManureForm`
    ) as ManureForm;

    const manureSystems = getValues(
      `stages.${stageIndex}.stageData.housing.manureSystems`
    ) as ManureManagementSystem[];

    // Share box is visible if there are >=2 MMS of same kind; solid or liquide
    if (currentLocalManureForm) {
      const count =
        manureSystems?.filter(
          (item) => item.localManureForm === currentLocalManureForm
        )?.length || 0;

      // if intervention, check additions as well
      let interventionCount = 0;
      if (isAdditions) {
        const manureSystemsAdditions = getValues(
          `stages.${stageIndex}.stageData.housing.manureSystemsAdditions`
        ) as ManureManagementSystem[];
        interventionCount =
          manureSystemsAdditions?.filter(
            (item) => item.localManureForm === currentLocalManureForm
          )?.length || 0;
      }

      return count + interventionCount > 1;
    }

    return false;
  };

  const filterMatchingStorage = (value: string) => {
    const selBaseName = value;
    if (selBaseName) {
      const newMatchingStorages =
        manureManagementSystems?.filter(
          (item) =>
            item.value.startsWith(selBaseName) && item.value.endsWith("MONTH")
        ) || [];
      newMatchingStorages.forEach((item) => {
        const currItem = item;
        const firstDigit = item.text.search(/\d/);
        // check if there is over / under in the name
        let substringIndex = firstDigit;
        const overIndex = item.text.lastIndexOf(" under");
        const underIndex = item.text.lastIndexOf(" over");
        if (overIndex > -1) substringIndex = overIndex;
        if (underIndex > -1) substringIndex = underIndex;
        currItem.text = item.text
          .substring(substringIndex)
          .replace("month", " month");
      });
      setMatchingStorages(newMatchingStorages);
      if (newMatchingStorages.length === 0) {
        setValue(`${fieldItemPrefix}.mmsHoldingDuration`, "");
      }
    }
  };

  const revalidateMMS = () => {
    formContext
      .trigger(
        `stages.${stageIndex}.stageData.housing.manureSystems${
          isAdditions ? "Additions" : ""
        }`
      )
      .then(() => {})
      .catch(() => {});
  };

  const onMMSChange = (e: any) => {
    const eventTarget = e.target;
    filterMatchingStorage(eventTarget.value as string);
    if (mmsChangeHandler) mmsChangeHandler();
    const localManureForm = getManureFormForSingleEntry(eventTarget.value);
    setValue(`${fieldItemPrefix}.localManureForm`, localManureForm);
    // if not showin share, set 100
    setValue(`${fieldItemPrefix}.share`, !showShare() ? "100" : "");
    revalidateMMS();
  };

  const onMMSChange2 = (e: any) => {
    const eventTarget = e.target;
    if (mmsChangeHandler) mmsChangeHandler();
    setValue(
      `${fieldItemPrefix}.localManureForm`,
      getManureFormForSingleEntry(eventTarget.value)
    );
    revalidateMMS();
  };

  const currentMMSCount = ((
    getValues(
      `stages.${stageIndex}.stageData.housing.manureSystems${
        isAdditions ? "Additions" : ""
      }`
    ) || []
  )?.length || 0) as number;
  const className =
    currentMMSCount > 1 || isAdditions
      ? classes.additionalEntriesBlockHolder
      : "";

  useEffect(() => {
    filterMatchingStorage(formContext.getValues(`${fieldItemPrefix}.mmsType`));
  }, []);

  return (
    <div className={className}>
      {removeHandler && (isAdditions || currentMMSCount > 1) && (
        <DsmButton
          variant="text"
          style={{ position: "relative", width: "2%", left: "98%" }}
          onClick={() => removeHandler(itemIndex)}
        >
          <DsmIcon
            name="general/x-close"
            style={{
              position: "absolute",
              color: "var(--dsm-color-neutral-darker",
              right: "0",
            }}
          />
        </DsmButton>
      )}
      <input
        ref={formContext.register()}
        type="hidden"
        name={`${fieldItemPrefix}.id`}
        defaultValue={formContext.getValues(`${fieldItemPrefix}.id`) || ""}
      />
      <input
        ref={formContext.register()}
        type="hidden"
        name={`${fieldItemPrefix}.localManureForm`}
        defaultValue={
          formContext.getValues(`${fieldItemPrefix}.localManureForm`) || ""
        }
      />
      <DsmGrid className={classes.dsmGridTwoColumn}>
        <ReactHookDsmSelect
          label={intl.formatMessage({ id: "SUSTELL.STAGE.PIGS.MANURE.MMS" })}
          name={`${fieldItemPrefix}.mmsType`}
          options={filteredMMSList}
          changeHandler={onMMSChange}
          required
          defaultValue={
            formContext.getValues(`${fieldItemPrefix}.mmsType`) ||
            ("" as string)
          }
          disabled={formType === FormType.View}
        />
        {matchingStorages && matchingStorages.length > 0 && (
          <ReactHookDsmSelect
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.PIGS.HOUSING.HOLDING_PERIOD",
            })}
            name={`${fieldItemPrefix}.mmsHoldingDuration`}
            options={matchingStorages}
            required
            defaultValue={
              formContext.getValues(`${fieldItemPrefix}.mmsHoldingDuration`) ||
              ""
            }
            disabled={formType === FormType.View}
            changeHandler={onMMSChange2}
          />
        )}
      </DsmGrid>
      {/* { (currentMMSCount > 1 || isAdditions) &&  */}
      {showShare() && (
        <DsmGrid className={classes.dsmGridTwoColumn}>
          <ReactHookDsmInput
            name={`${fieldItemPrefix}.share`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.PIGS.HOUSING.MMS.SHARE",
            })}
            disabled={formType === FormType.View}
            adornment="%"
            required
            defaultValue={
              formContext.getValues(`${fieldItemPrefix}.share`) || ""
            }
            changeHandler={revalidateMMS}
            type="number"
          />
          <div />
        </DsmGrid>
      )}
    </div>
  );
};

export default MMSBlockComponent;
